import { HStack } from '@chakra-ui/react'
import OrgTeamBanner from 'components/DashboardBanners/OrgTeamBanner'
import BannerActionButton from 'components/DashboardBanners/OrgTeamBanner/banner-action-button'
import SvgIcon from 'components/SvgIcon'
import { resolveOrgAccent } from 'utils/banner-gradient'

interface IOrgContextBannerProps {
  orgName: string
  orgLogo?: string
  /** Organization.primaryColor — drives the gradient, ring, glow and label colour. */
  primaryColor?: string
  sports: string[]
  teamCount?: number
  memberCount?: number
  /** Omitted where the page puts its switchers outside the banner. */
  onSwitchOrg?: () => void
  /**
   * Team context, for the pages whose data belongs to a Team (Schedule,
   * Assignments). Omitted on organization-level pages, which then render exactly
   * the banner they render today — one action, no Team line.
   */
  teamName?: string
  onSwitchTeam?: () => void
}

const pluralize = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? '' : 's'}`

/**
 * Organization context banner — the header of both the Organization page and the
 * Teams page, so the user always knows which organization they are looking at.
 *
 * Thin wrapper over the dashboard banner: the surface, gradient and type scale are
 * already the design's, so this only supplies the team/member counts in the label
 * and the Switch Org action.
 */
const OrgContextBanner = ({
  orgName,
  orgLogo,
  primaryColor,
  sports,
  teamCount,
  memberCount,
  onSwitchOrg,
  teamName,
  onSwitchTeam
}: IOrgContextBannerProps) => {
  // Counts are omitted while unknown rather than shown as 0 — a freshly loaded page
  // would otherwise claim the organization has no teams and no members.
  const metaSegments = [
    teamCount === undefined ? '' : pluralize(teamCount, 'team'),
    memberCount === undefined ? '' : pluralize(memberCount, 'member')
  ]

  return (
    <OrgTeamBanner
      orgName={orgName}
      // The Team is the headline once one is in context, with the organization
      // kept as the small label above it — the same shape the dashboard uses, so
      // the two banners do not disagree about what the big word means.
      title={teamName ?? orgName}
      logoUrl={orgLogo}
      primaryColor={primaryColor}
      sports={sports}
      metaSegments={metaSegments}
      action={
        onSwitchOrg || onSwitchTeam ? (
          <HStack spacing="8px">
            {onSwitchOrg && (
              <BannerActionButton
                accent={resolveOrgAccent(primaryColor)}
                label="Switch Org"
                onClick={onSwitchOrg}
                icon={<SvgIcon iconName="cuida_refresh-outline" size={14} color="white" />}
              />
            )}
            {onSwitchTeam && (
              <BannerActionButton
                accent={resolveOrgAccent(primaryColor)}
                label="Switch Team"
                onClick={onSwitchTeam}
                icon={<SvgIcon iconName="cuida_refresh-outline" size={14} color="white" />}
              />
            )}
          </HStack>
        ) : undefined
      }
    />
  )
}

export default OrgContextBanner
